import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Collapse, IconButton, Chip } from "@mui/material";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useSelector } from "react-redux";
import CustomTableHeading from '../CommonComponents/CustomTableHeading';
import CustomTableMetric from '../CommonComponents/CustomTableMetric';
import CustomDialogBox from "../CommonComponents/CustomDialogBox";
import "../../cssfiles/homepage.css";
import "../../cssfiles/commissionconfig.css";

// Import your API service functions
import { 
    loginToCommission,
    syncCommissionData
} from '../../Restclient/datacollection';

export default function CommissionIntegration() {
    
    const themechange = useSelector((state) => state.themedata.theme);
    const userdetails = useSelector((state) => state.logindata.userdetails);
    
    // State for login
    const [commissionUsername, setCommissionUsername] = useState('');
    const [commissionPassword, setCommissionPassword] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [commissionToken, setCommissionToken] = useState('');
    
    // State for commission data
    const [commissionData, setCommissionData] = useState(null);
    const [ioModules, setIoModules] = useState([]);
    const [expandedModules, setExpandedModules] = useState({});
    const [selectedModules, setSelectedModules] = useState({});
    
    // State for sync results
    const [syncResults, setSyncResults] = useState(null);
    
    // UI state
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [successDialog, setSuccessDialog] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorDialog, setErrorDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Handle login to Commission
    const handleLogin = () => {
        if (!commissionUsername || !commissionPassword) {
            setErrorMessage('Please enter both username and password');
            setErrorDialog(true);
            return;
        }

        setLoading(true);
        
        const loginData = {
            userName: commissionUsername,
            password: commissionPassword
        };

        loginToCommission(loginData, (response, status) => {
            setLoading(false);
            
            if (status === 200 && response?.data?.success) {
                setIsLoggedIn(true);
                setCommissionToken(response.data.token);
                setCommissionData(response.data);
                setIoModules(response.data.ioModulesWithDevices || []);
                
                // Initialize all modules as selected by default
                const initialSelection = {};
                (response.data.ioModulesWithDevices || []).forEach((module, index) => {
                    initialSelection[index] = true;
                });
                setSelectedModules(initialSelection);
                
                setSuccessMessage(`Successfully connected to Commission. Found ${response.data.ioModulesWithDevices?.length || 0} IO Modules.`);
                setSuccessDialog(true);
            } else {
                setErrorMessage(response?.data?.message || 'Failed to login to Commission');
                setErrorDialog(true);
            }
        });
    };

    // Handle logout/reset
    const handleLogout = () => {
        setIsLoggedIn(false);
        setCommissionToken('');
        setCommissionData(null);
        setIoModules([]);
        setSelectedModules({});
        setExpandedModules({});
        setSyncResults(null);
        setCommissionPassword('');
    };

    // Toggle module expansion
    const toggleModuleExpansion = (index) => {
        setExpandedModules(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    // Toggle module selection
    const toggleModuleSelection = (index) => {
        setSelectedModules(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    // Handle sync to Operate
    const handleSync = () => {
        // Filter only selected modules
        const selectedIoModules = ioModules.filter((_, index) => selectedModules[index]);
        
        if (selectedIoModules.length === 0) {
            setErrorMessage('Please select at least one IO Module to sync');
            setErrorDialog(true);
            return;
        }

        setSyncing(true);
        
        const syncData = {
            ioModulesWithDevices: selectedIoModules
        };

        syncCommissionData(syncData, (response, status) => {
            setSyncing(false);
            
            if (status === 200 && response?.data?.success) {
                setSyncResults(response.data);
                setSuccessMessage(
                    `Sync completed! Created ${response.data.summary?.gatewaysCreated || 0} gateways and ${response.data.summary?.devicesCreated || 0} devices.`
                );
                setSuccessDialog(true);
            } else {
                setErrorMessage(response?.data?.message || 'Failed to sync Commission data');
                setErrorDialog(true);
            }
        });
    };

    // Count total devices in a module
    const getDeviceCount = (module) => {
        return module.devices?.length || 0;
    };

    // Count total tags in a module
    const getTagCount = (module) => {
        return module.devices?.reduce((total, device) => {
            return total + (device.configString?.length || 0);
        }, 0) || 0;
    };

    return (
        <Box className="commission-config-container">
            {!isLoggedIn ? (
                // Login View
                <>
                    <Box className="commission-config-header">
                        <Typography className="commission-config-title">
                            SmartComm Commission Integration
                        </Typography>
                        <Typography className="commission-config-subtitle">
                            Login to Commission to import IO Modules, Devices, and Configuration Tags
                        </Typography>
                    </Box>

                    <Box className="commission-login-section">
                        <TextField
                            label="Commission Username"
                            value={commissionUsername}
                            onChange={(e) => setCommissionUsername(e.target.value)}
                            className={`commission-config-textfield commission-config-textfield-${themechange}`}
                            variant="outlined"
                            fullWidth
                            disabled={loading}
                            margin="normal"
                        />
                        <TextField
                            label="Commission Password"
                            type="password"
                            value={commissionPassword}
                            onChange={(e) => setCommissionPassword(e.target.value)}
                            className={`commission-config-textfield commission-config-textfield-${themechange}`}
                            variant="outlined"
                            fullWidth
                            disabled={loading}
                            margin="normal"
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleLogin();
                                }
                            }}
                        />
                        <Button
                            onClick={handleLogin}
                            disabled={loading || !commissionUsername || !commissionPassword}
                            className="commission-login-button"
                            variant="contained"
                            fullWidth
                        >
                            {loading ? 'Connecting...' : 'Connect to Commission'}
                        </Button>
                    </Box>
                </>
            ) : (
                // Review & Sync View
                <>
                    <Box className="commission-config-header">
                        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography className="commission-config-title">
                                    Commission Data Review
                                </Typography>
                                <Typography className="commission-config-subtitle">
                                    Review and select IO Modules to sync to Operate
                                </Typography>
                            </Box>
                            <Button
                                onClick={handleLogout}
                                className="commission-logout-button"
                                variant="outlined"
                            >
                                Disconnect
                            </Button>
                        </Box>
                    </Box>

                    {/* Summary Cards */}
                    <Box className="commission-summary-section">
                        <Box className="commission-summary-card">
                            <Typography className="commission-summary-label">IO Modules</Typography>
                            <Typography className="commission-summary-value">{ioModules.length}</Typography>
                        </Box>
                        <Box className="commission-summary-card">
                            <Typography className="commission-summary-label">Total Devices</Typography>
                            <Typography className="commission-summary-value">
                                {ioModules.reduce((total, module) => total + getDeviceCount(module), 0)}
                            </Typography>
                        </Box>
                        <Box className="commission-summary-card">
                            <Typography className="commission-summary-label">Total Tags</Typography>
                            <Typography className="commission-summary-value">
                                {ioModules.reduce((total, module) => total + getTagCount(module), 0)}
                            </Typography>
                        </Box>
                        <Box className="commission-summary-card">
                            <Typography className="commission-summary-label">Selected</Typography>
                            <Typography className="commission-summary-value">
                                {Object.values(selectedModules).filter(Boolean).length}
                            </Typography>
                        </Box>
                    </Box>

                    {/* IO Modules Table */}
                    <Box className="commission-config-table-wrapper">
                        <TableContainer component={Paper} className="commission-config-table-container" elevation={0}>
                            <Table>
                                <TableHead>
                                    <TableRow className="commission-config-table-header-row">
                                        <TableCell align="center" width="60px">
                                            <CustomTableHeading children={'Select'} />
                                        </TableCell>
                                        <TableCell align="center" width="60px">
                                            <CustomTableHeading children={'Expand'} />
                                        </TableCell>
                                        <TableCell align="left">
                                            <CustomTableHeading children={'IO Module Name'} />
                                        </TableCell>
                                        <TableCell align="left">
                                            <CustomTableHeading children={'Project'} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <CustomTableHeading children={'Devices'} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <CustomTableHeading children={'Tags'} />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {ioModules.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" className="commission-config-empty-cell">
                                                <Typography className="commission-config-empty-text">
                                                    No IO Modules found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        ioModules.map((module, moduleIndex) => (
                                            <React.Fragment key={moduleIndex}>
                                                <TableRow className="commission-config-table-row">
                                                    <TableCell align="center">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedModules[moduleIndex] || false}
                                                            onChange={() => toggleModuleSelection(moduleIndex)}
                                                            className="commission-checkbox"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleModuleExpansion(moduleIndex)}
                                                        >
                                                            {expandedModules[moduleIndex] ? 
                                                                <ExpandLessIcon fontSize="small" /> : 
                                                                <ExpandMoreIcon fontSize="small" />
                                                            }
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell>
                                                        <CustomTableMetric children={module.Name || 'Unnamed Module'} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <CustomTableMetric children={module.projectName || '-'} />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip 
                                                            label={getDeviceCount(module)} 
                                                            size="small" 
                                                            color="primary" 
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip 
                                                            label={getTagCount(module)} 
                                                            size="small" 
                                                            color="secondary" 
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                                
                                                {/* Expanded Device Details */}
                                                <TableRow>
                                                    <TableCell 
                                                        colSpan={6} 
                                                        style={{ paddingBottom: 0, paddingTop: 0, border: 0 }}
                                                    >
                                                        <Collapse in={expandedModules[moduleIndex]} timeout="auto" unmountOnExit>
                                                            <Box className="commission-device-details">
                                                                <Typography className="commission-device-details-title">
                                                                    Devices in {module.Name}
                                                                </Typography>
                                                                <Table size="small">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell><CustomTableHeading children={'Device Name'} /></TableCell>
                                                                            <TableCell><CustomTableHeading children={'Category'} /></TableCell>
                                                                            <TableCell><CustomTableHeading children={'Sub-Category'} /></TableCell>
                                                                            <TableCell><CustomTableHeading children={'Interface'} /></TableCell>
                                                                            <TableCell><CustomTableHeading children={'Serial No'} /></TableCell>
                                                                            <TableCell align="center"><CustomTableHeading children={'Tags'} /></TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {module.devices?.map((device, deviceIndex) => (
                                                                            <TableRow key={deviceIndex}>
                                                                                <TableCell>
                                                                                    <CustomTableMetric children={device.Name || 'Unnamed Device'} />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <CustomTableMetric children={device.deviceCategory || '-'} />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <CustomTableMetric children={device.deviceSubCategory || '-'} />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <CustomTableMetric children={device.interfaceType || '-'} />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <CustomTableMetric children={device.serialNumber || '-'} />
                                                                                </TableCell>
                                                                                <TableCell align="center">
                                                                                    <Chip 
                                                                                        label={device.configString?.length || 0} 
                                                                                        size="small" 
                                                                                        color="info"
                                                                                    />
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* Sync Button */}
                    <Box className="commission-sync-section">
                        <Button
                            onClick={handleSync}
                            disabled={syncing || Object.values(selectedModules).filter(Boolean).length === 0}
                            className="commission-sync-button"
                            variant="contained"
                            size="large"
                        >
                            {syncing ? 'Syncing to Operate...' : `Sync ${Object.values(selectedModules).filter(Boolean).length} Selected Module(s) to Operate`}
                        </Button>
                    </Box>

                    {/* Sync Results */}
                    {syncResults && (
                        <Box className="commission-results-section">
                            <Typography className="commission-results-title">
                                Sync Results
                            </Typography>
                            <Box className="commission-summary-section">
                                <Box className="commission-summary-card success">
                                    <Typography className="commission-summary-label">Gateways Created</Typography>
                                    <Typography className="commission-summary-value">{syncResults.summary?.gatewaysCreated || 0}</Typography>
                                </Box>
                                <Box className="commission-summary-card success">
                                    <Typography className="commission-summary-label">Devices Created</Typography>
                                    <Typography className="commission-summary-value">{syncResults.summary?.devicesCreated || 0}</Typography>
                                </Box>
                                <Box className="commission-summary-card success">
                                    <Typography className="commission-summary-label">Tags Created</Typography>
                                    <Typography className="commission-summary-value">{syncResults.summary?.tagsCreated || 0}</Typography>
                                </Box>
                                {(syncResults.summary?.gatewaysFailed > 0 || syncResults.summary?.devicesFailed > 0) && (
                                    <Box className="commission-summary-card error">
                                        <Typography className="commission-summary-label">Failed</Typography>
                                        <Typography className="commission-summary-value">
                                            {(syncResults.summary?.gatewaysFailed || 0) + (syncResults.summary?.devicesFailed || 0)}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </>
            )}

            {/* Dialog boxes */}
            <CustomDialogBox
                dialogTitle={"Success"}
                dialogText={successMessage}
                openDialog={successDialog}
                handleCloseDialog={() => setSuccessDialog(false)}
                handleConfirmDialog={() => setSuccessDialog(false)}
                confirmText={"OK"}
                type={"Success"}
            />

            <CustomDialogBox
                dialogTitle={"Error"}
                dialogText={errorMessage}
                openDialog={errorDialog}
                handleCloseDialog={() => setErrorDialog(false)}
                handleConfirmDialog={() => setErrorDialog(false)}
                confirmText={"OK"}
                type={"Error"}
            />
        </Box>
    );
}
